/* DealScorePanel — Score de réalisabilité (jugement banque d'affaires).
 * Noyau universel (Prix, Complétude, Sécurisation, Backing, Négociabilité)
 * + lentille spécifique à la classe d'actif. Fonctionne pour tout deal du
 * registre (endpoint = /workspace/deals/{id} ou /workspace/courtage/mandates/{mid}).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Gauge, Loader2, RefreshCw, AlertTriangle, ArrowUpRight, Handshake, ShieldCheck, Focus, Paperclip, FileCheck } from "lucide-react";

const FALLBACK_LABELS = {
  prix: "Prix", completude: "Complétude", securisation: "Sécurisation",
  backing: "Backing", negociabilite: "Négociabilité",
};

function scoreColor(s) {
  if (s >= 70) return "#1D9E75";
  if (s >= 45) return "#EF9F27";
  return "#E24B4A";
}

export default function DealScorePanel({ endpoint }) {
  const [score, setScore] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`${endpoint}/score`);
      setScore(r.data?.deal_score || null);
    } catch { /* silencieux */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  // Poll pendant le calcul
  useEffect(() => {
    if (score?.status !== "running") return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [score?.status]);

  const compute = async () => {
    setBusy(true);
    try {
      await api.post(`${endpoint}/score`);
      setScore({ status: "running" });
      notify.success("Analyse en cours — le comité rend son verdict dans ~30 secondes.");
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };

  const toggleItem = async (idx, provided) => {
    try {
      const r = await api.patch(`${endpoint}/score-checklist/${idx}`, { provided });
      setScore((s) => ({ ...s, checklist: r.data.checklist, structural: r.data.structural }));
      notify.success(provided ? "Pièce validée (DECLARED) : la note structurelle monte." : "Pièce retirée : la note redescend.");
    } catch (e) { notify.error(fmtError(e)); }
  };

  const [picker, setPicker] = useState(null);
  const [docSearch, setDocSearch] = useState("");
  const [docList, setDocList] = useState([]);

  const openPicker = async (idx) => {
    setPicker(idx); setDocSearch("");
    try {
      const r = await api.get("/workspace/documents", { params: { limit: 30 } });
      setDocList(Array.isArray(r.data) ? r.data : r.data?.items || []);
    } catch { setDocList([]); }
  };

  const searchDocs = async (q) => {
    setDocSearch(q);
    try {
      const r = await api.get("/workspace/documents", { params: { search: q || undefined, limit: 30 } });
      setDocList(Array.isArray(r.data) ? r.data : r.data?.items || []);
    } catch { /* silencieux */ }
  };

  const attachEvidence = async (idx, docId) => {
    try {
      const r = await api.post(`${endpoint}/score-checklist/${idx}/evidence`, { document_ids: [docId] });
      setScore((s) => ({ ...s, checklist: r.data.checklist, structural: r.data.structural }));
      setPicker(null);
      notify.success(r.data.rescoring
        ? "Preuve jointe : pièce DOCUMENT_VERIFIED. Le comité recalcule le scoring avec la preuve."
        : "Preuve jointe : pièce DOCUMENT_VERIFIED.");
      if (r.data.rescoring) setTimeout(load, 4000);
    } catch (e) { notify.error(fmtError(e)); }
  };

  const detachEvidence = async (idx, docId) => {
    try {
      const r = await api.delete(`${endpoint}/score-checklist/${idx}/evidence/${docId}`);
      setScore((s) => ({ ...s, checklist: r.data.checklist }));
      notify.success("Preuve retirée.");
    } catch (e) { notify.error(fmtError(e)); }
  };

  const [uploading, setUploading] = useState(false);
  const uploadEvidence = async (idx, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("module_source", "manual");
      fd.append("linked_module", "deals");
      fd.append("linked_entity_id", endpoint.split("/").pop());
      fd.append("description", "Preuve checklist de scoring");
      const up = await api.post("/workspace/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await attachEvidence(idx, up.data.id);
    } catch (e) { notify.error(fmtError(e)); }
    setUploading(false);
  };

  const printChecklist = () => {
    const rows = (score.checklist || []).map((it) => `
      <tr>
        <td class="chk">${it.provided ? "☑" : "☐"}</td>
        <td>${it.label}${it.verification === "DOCUMENT_VERIFIED" ? ' <span class="ok">Document vérifié</span>' : it.verification === "DECLARED" ? ' <span class="warn">Déclaré</span>' : ""}</td>
        <td class="pts">+${it.points} pts</td>
      </tr>`).join("");
    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"><title>Checklist — ${score.asset_class || ""}</title>
      <style>
        body{font-family:Segoe UI,Helvetica,Arial,sans-serif;color:#16181d;margin:40px;line-height:1.5}
        .ov{font-size:10px;letter-spacing:.3em;text-transform:uppercase;color:#378ADD}
        h1{font-size:22px;margin:6px 0 2px} .sub{color:#555;font-size:13px}
        .scores{display:flex;gap:32px;margin:22px 0;padding:16px 20px;border:1px solid #ddd;border-radius:8px}
        .scores div{text-align:center} .scores .n{font-size:26px;font-weight:800} .scores .l{font-size:10px;text-transform:uppercase;letter-spacing:.15em;color:#777}
        table{width:100%;border-collapse:collapse;margin-top:8px;font-size:13px}
        td{padding:10px 8px;border-bottom:1px solid #e5e5e5;vertical-align:top}
        .chk{font-size:16px;width:28px} .pts{white-space:nowrap;font-weight:700;color:#1D9E75;text-align:right}
        .ok{color:#1D9E75;font-size:10px;text-transform:uppercase;letter-spacing:.1em}
        .warn{color:#b97a10;font-size:10px;text-transform:uppercase;letter-spacing:.1em}
        .note{margin-top:20px;font-size:11px;color:#777}
        footer{margin-top:36px;padding-top:12px;border-top:1px solid #ddd;font-size:10px;color:#999;display:flex;justify-content:space-between}
      </style></head><body>
      <div class="ov">Noam · Checklist structurelle du dossier</div>
      <h1>${document.title || "Dossier"} — pièces attendues</h1>
      <div class="sub">${score.verdict || ""}</div>
      <div class="scores">
        <div><div class="n">${score.structural?.global ?? score.global}/100</div><div class="l">Note actuelle (${score.structural?.grade || score.rating?.grade || ""})</div></div>
        <div><div class="n">→</div><div class="l"></div></div>
        <div><div class="n">${score.projected?.global ?? ""}/100</div><div class="l">Potentiel si pièces fournies (${score.rating?.projected_grade || ""})</div></div>
      </div>
      <table>${rows}</table>
      <div class="note">${score.projected?.comment || ""}<br/>Chaque pièce fournie et vérifiée fait monter la note du nombre de points indiqué. Une pièce jointe avec document = « Document vérifié » ; une déclaration sans document reste « Déclaré ».</div>
      <footer><span>NOAM PLATFORM · Édité le ${new Date().toLocaleDateString("fr-CH")}</span><span>Confidentiel</span></footer>
      <script>window.print()</script></body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); }
  };

  const done = score?.status === "done";
  const dims = done ? Object.entries(score.dimensions || {}) : [];
  const coreDims = dims.filter(([, d]) => !d.lens);
  const lensDims = dims.filter(([, d]) => d.lens);

  const renderDim = ([k, d]) => (
    <div key={k} data-testid={`dealscore-dim-${k}`}>
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-zinc-300 font-medium flex items-center gap-1.5">
          {d.lens && <Focus className="w-3 h-3 text-[#378ADD]" />}
          {d.label || FALLBACK_LABELS[k] || k}
        </span>
        <span className="font-mono" style={{ color: scoreColor(d.score) }}>{d.score}</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 mt-1">
        <div className="h-full rounded-full transition-all duration-700"
          style={{ width: `${d.score}%`, background: scoreColor(d.score) }} />
      </div>
      {d.comment && <div className="text-[10px] text-zinc-500 mt-0.5">{d.comment}</div>}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="dealscore-root">
      <div className="noam-card p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-medium text-zinc-100 flex items-center gap-2">
              <Gauge className="w-4 h-4 text-[#EFD127]" /> Score de réalisabilité
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              Le deal jugé comme le ferait une banque d'affaires : noyau universel + lentille de la classe d'actif.
              {done && ` Calculé le ${(score.computed_at || "").slice(0, 16).replace("T", " ")}.`}
            </div>
          </div>
          <button onClick={compute} disabled={busy || score?.status === "running"}
            className="btn-primary text-xs disabled:opacity-50" data-testid="dealscore-compute-btn">
            {score?.status === "running" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {done ? "Recalculer" : "Analyser le deal"}
          </button>
        </div>

        {score?.status === "running" && (
          <div className="mt-4 text-xs text-[#378ADD] flex items-center gap-2" data-testid="dealscore-running">
            <Loader2 className="w-4 h-4 animate-spin" /> Le comité analyse la fiche, les documents, les NDA et le suivi des contreparties…
          </div>
        )}
        {score?.status === "failed" && (
          <div className="mt-4 text-xs text-[#E24B4A] flex items-center gap-2" data-testid="dealscore-failed">
            <AlertTriangle className="w-4 h-4" /> Analyse impossible : {score.error}
          </div>
        )}
        {!score && (
          <div className="mt-4 text-xs text-zinc-600">Aucun score encore. Lancez l'analyse pour obtenir le verdict et les actions de repositionnement.</div>
        )}

        {done && (
          <div className="mt-5 grid grid-cols-12 gap-5">
            {/* Score global */}
            <div className="col-span-12 md:col-span-3 flex flex-col items-center justify-center gap-2">
              <div className="relative w-28 h-28 rounded-full flex items-center justify-center"
                style={{ background: `conic-gradient(${scoreColor(score.global)} ${score.global * 3.6}deg, rgba(255,255,255,0.06) 0deg)` }}
                data-testid="dealscore-global">
                <div className="w-[88px] h-[88px] rounded-full bg-[#0D131B] flex flex-col items-center justify-center">
                  <span className="noam-display text-3xl font-bold" style={{ color: scoreColor(score.global) }}>{score.global}</span>
                  <span className="text-[9px] uppercase tracking-widest text-zinc-500">/100</span>
                </div>
              </div>
              {score.rating?.grade && (
                <div className="text-center" data-testid="dealscore-rating">
                  <span className="noam-display text-2xl font-bold tracking-tight" style={{ color: scoreColor(score.global) }}>{score.rating.grade}</span>
                  {score.rating.projected_grade && score.rating.projected_grade !== score.rating.grade && (
                    <span className="text-sm text-[#43C6D8] ml-1.5">→ {score.rating.projected_grade}</span>
                  )}
                  <div className="text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">
                    Perspective {score.rating.outlook === "positive" ? "positive" : score.rating.outlook === "negative" ? "négative" : "stable"}
                  </div>
                  <div className="text-[9px] text-zinc-600 mt-0.5 max-w-[140px]">{score.rating.label}</div>
                </div>
              )}
              <div className={`text-[10px] px-2 py-1 rounded-full border flex items-center gap-1 ${score.matchmaking_ready ? "border-[#1D9E75]/40 text-[#1D9E75] bg-[#1D9E75]/10" : "border-[#EF9F27]/40 text-[#EF9F27] bg-[#EF9F27]/10"}`}
                data-testid="dealscore-matchready">
                <Handshake className="w-3 h-3" /> {score.matchmaking_ready ? "Prêt pour le matchmaking" : "À repositionner avant matchmaking"}
              </div>
              {score.projected?.global > score.global && (
                <div className="text-center" data-testid="dealscore-projected">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Potentiel si dossier complété</div>
                  <div className="noam-display text-xl font-bold mt-0.5" style={{ color: scoreColor(score.projected.global) }}>
                    → {score.projected.global}<span className="text-[10px] text-zinc-500">/100</span>
                  </div>
                </div>
              )}
            </div>
            {/* Dimensions */}
            <div className="col-span-12 md:col-span-9 space-y-2.5">
              <div className="text-xs text-zinc-200 italic border-l-2 border-[#EFD127] pl-3" data-testid="dealscore-verdict">
                « {score.verdict} »
              </div>
              {coreDims.map(renderDim)}
              {lensDims.length > 0 && (
                <div className="text-[9px] uppercase tracking-widest text-[#378ADD] pt-1.5 flex items-center gap-1.5">
                  <Focus className="w-3 h-3" /> Lentille {`— ${score.asset_class ? score.asset_class.replace(/_/g, " ") : "actif"}`}
                </div>
              )}
              {lensDims.map(renderDim)}
            </div>
          </div>
        )}
      </div>

      {done && score.rating?.grade && (
        <div className="noam-card p-4" data-testid="dealscore-rating-scale">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Échelle Noam de réalisabilité</div>
            {score.rating.outlook_comment && (
              <div className="text-[10px] text-zinc-400 italic">{score.rating.outlook_comment}</div>
            )}
          </div>
          <div className="flex mt-3 rounded overflow-hidden border border-white/5">
            {["AAA", "AA", "A", "BBB", "BB", "B", "CCC", "CC", "C", "D"].map((g) => {
              const active = g === score.rating.grade;
              const projected = g === score.rating.projected_grade && !active;
              return (
                <div key={g}
                  className={`flex-1 text-center py-1.5 text-[10px] font-mono ${active ? "bg-white text-zinc-900 font-bold" : projected ? "bg-[#43C6D8]/25 text-[#43C6D8] font-bold" : "bg-white/[0.02] text-zinc-600"}`}
                  data-testid={`rating-scale-${g}`}>
                  {g}{projected ? " ◇" : ""}
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-1.5 text-[9px] text-zinc-600">
            <span>Bancable</span><span>◇ = grade projeté si dossier complété</span><span>Sans substance</span>
          </div>
        </div>
      )}

      {done && score.economics && (score.economics.value_potential_chf > 0 || score.economics.inaction_cost_chf_monthly > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="dealscore-economics">
          <div className="noam-card p-4 border-l-2 border-l-[#1D9E75]">
            <div className="text-[10px] uppercase tracking-widest text-[#1D9E75]">Valeur potentielle (estimation)</div>
            <div className="noam-display text-2xl font-bold text-white mt-1" data-testid="dealscore-value-potential">
              {score.economics.value_potential_chf > 0 ? `CHF ${Math.round(score.economics.value_potential_chf).toLocaleString("fr-CH")}` : "Non quantifiable"}
            </div>
            <p className="text-[11px] text-zinc-400 mt-1.5">{score.economics.value_comment}</p>
          </div>
          <div className="noam-card p-4 border-l-2 border-l-[#E24B4A]">
            <div className="text-[10px] uppercase tracking-widest text-[#E24B4A]">Coût de l'inaction (estimation)</div>
            <div className="noam-display text-2xl font-bold text-white mt-1" data-testid="dealscore-inaction-cost">
              {score.economics.inaction_cost_chf_monthly > 0 ? `CHF ${Math.round(score.economics.inaction_cost_chf_monthly).toLocaleString("fr-CH")} / mois` : "Non quantifiable"}
            </div>
            <p className="text-[11px] text-zinc-400 mt-1.5">{score.economics.inaction_comment}</p>
            <p className="text-[9px] text-zinc-600 mt-2 uppercase tracking-wider">{score.economics.disclaimer}</p>
          </div>
        </div>
      )}

      {done && score.checklist?.length > 0 && (
        <div className="noam-card p-4 border-l-2 border-l-[#43C6D8]" data-testid="dealscore-projected-conditions">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-[10px] uppercase tracking-widest text-[#43C6D8]">
              Checklist structurelle — note actuelle {score.structural?.global ?? score.global}/100
              {score.structural?.grade ? ` (${score.structural.grade})` : ""} → potentiel {score.projected?.global}/100
            </div>
            <div className="flex items-center gap-3">
              <button onClick={printChecklist}
                className="text-[10px] text-zinc-400 hover:text-white border border-zinc-700 hover:border-zinc-500 rounded px-2 py-1 transition-colors"
                data-testid="dealscore-print-checklist">
                Imprimer / Envoyer
              </button>
              <div className="text-[10px] text-zinc-500">{score.structural?.earned_points || 0} pts gagnés</div>
            </div>
          </div>
          {score.projected?.comment && <p className="text-[11px] text-zinc-400 italic mt-1.5">{score.projected.comment}</p>}
          <div className="h-1.5 rounded-full bg-white/5 mt-2.5">
            <div className="h-full rounded-full bg-[#43C6D8] transition-all duration-700"
              style={{ width: `${score.projected?.global ? Math.min(100, ((score.structural?.global ?? score.global) / score.projected.global) * 100) : 0}%` }} />
          </div>
          <div className="mt-3 space-y-1.5">
            {score.checklist.map((it) => (
              <div key={it.index} data-testid={`dealscore-checklist-${it.index}`}>
                <div className="flex items-start gap-2 group">
                  <input type="checkbox" checked={!!it.provided}
                    onChange={(e) => toggleItem(it.index, e.target.checked)}
                    className="mt-0.5 accent-[#43C6D8]"
                    data-testid={`dealscore-checklist-toggle-${it.index}`} />
                  <span className={`text-[11px] leading-relaxed flex-1 ${it.provided ? "text-zinc-500" : "text-zinc-300 group-hover:text-white"}`}>
                    {it.label}
                    {it.provided && (
                      <span className={`ml-2 text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${it.verification === "DOCUMENT_VERIFIED" ? "border-[#1D9E75]/50 text-[#1D9E75]" : "border-[#EF9F27]/50 text-[#EF9F27]"}`}
                        data-testid={`dealscore-verification-${it.index}`}>
                        {it.verification === "DOCUMENT_VERIFIED" ? "Document vérifié" : "Déclaré"}
                      </span>
                    )}
                  </span>
                  <button onClick={() => (picker === it.index ? setPicker(null) : openPicker(it.index))}
                    className="text-zinc-500 hover:text-[#43C6D8] shrink-0" title="Joindre une preuve documentaire"
                    data-testid={`dealscore-attach-${it.index}`}>
                    <Paperclip className="w-3.5 h-3.5" />
                  </button>
                  <span className={`text-[10px] font-mono font-bold shrink-0 ${it.provided ? "text-[#1D9E75]" : "text-[#43C6D8]"}`}>+{it.points} pts</span>
                </div>
                {(it.evidence || []).length > 0 && (
                  <div className="ml-6 mt-1 flex flex-wrap gap-1.5">
                    {it.evidence.map((ev) => (
                      <span key={ev.document_id} className="inline-flex items-center gap-1 text-[9px] text-[#1D9E75] border border-[#1D9E75]/30 rounded px-1.5 py-0.5"
                        data-testid={`dealscore-evidence-${it.index}-${ev.document_id}`}>
                        <FileCheck className="w-2.5 h-2.5" /> {ev.name}
                        <button onClick={() => detachEvidence(it.index, ev.document_id)} className="text-zinc-500 hover:text-red-400 ml-0.5">×</button>
                      </span>
                    ))}
                  </div>
                )}
                {picker === it.index && (
                  <div className="ml-6 mt-1.5 border border-zinc-800 rounded p-2 bg-black/40" data-testid={`dealscore-picker-${it.index}`}>
                    <div className="flex items-center gap-2">
                      <input value={docSearch} onChange={(e) => searchDocs(e.target.value)}
                        placeholder="Rechercher un document du Drive…"
                        className="flex-1 bg-transparent border border-zinc-800 rounded px-2 py-1 text-[11px] text-zinc-200 outline-none focus:border-[#43C6D8]/50"
                        data-testid={`dealscore-picker-search-${it.index}`} />
                      <label className={`text-[10px] px-2 py-1 rounded border cursor-pointer transition-colors ${uploading ? "border-zinc-700 text-zinc-600" : "border-[#43C6D8]/40 text-[#43C6D8] hover:border-[#43C6D8] hover:text-white"}`}
                        data-testid={`dealscore-upload-${it.index}`}>
                        {uploading ? "Envoi…" : "Déposer un fichier"}
                        <input type="file" className="hidden" disabled={uploading}
                          onChange={(e) => uploadEvidence(it.index, e.target.files?.[0])} />
                      </label>
                    </div>
                    <div className="max-h-32 overflow-y-auto mt-1.5 space-y-0.5">
                      {docList.length === 0 && <div className="text-[10px] text-zinc-600 py-1">Aucun document. Déposez d'abord la pièce dans le Drive.</div>}
                      {docList.map((doc) => (
                        <button key={doc.id} onClick={() => attachEvidence(it.index, doc.id)}
                          className="block w-full text-left text-[11px] text-zinc-300 hover:text-white hover:bg-white/5 rounded px-1.5 py-1"
                          data-testid={`dealscore-pick-doc-${doc.id}`}>
                          {doc.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <p className="text-[9px] text-zinc-600 mt-2.5 uppercase tracking-wider">Case cochée sans preuve = Déclaré. Preuve jointe = Document vérifié + recalcul du comité avec la pièce.</p>
        </div>
      )}

      {done && !score.checklist?.length && score.projected?.conditions?.length > 0 && (
        <div className="noam-card p-4 border-l-2 border-l-[#43C6D8]" data-testid="dealscore-projected-conditions">
          <div className="text-[10px] uppercase tracking-widest text-[#43C6D8] flex items-center gap-1.5">
            Score projeté : {score.projected.global}/100
          </div>
          <p className="text-[11px] text-zinc-400 italic mt-1.5">{score.projected.comment}</p>
          <div className="mt-2 space-y-1">
            {score.projected.conditions.map((c, i) => (
              <div key={i} className="text-[11px] text-zinc-300 flex items-start gap-1.5">
                <span className="text-[#43C6D8] mt-0.5">◇</span> {c}
              </div>
            ))}
          </div>
        </div>
      )}

      {done && (score.faiblesses?.length > 0 || score.actions?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {score.faiblesses?.length > 0 && (
            <div className="noam-card p-4" data-testid="dealscore-weaknesses">
              <div className="text-[10px] uppercase tracking-widest text-[#E24B4A] mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> Ce qui affaiblit le deal
              </div>
              <ul className="space-y-1.5">
                {score.faiblesses.map((f, i) => (
                  <li key={i} className="text-xs text-zinc-300 flex gap-2"><span className="text-[#E24B4A]">·</span>{f}</li>
                ))}
              </ul>
            </div>
          )}
          {score.actions?.length > 0 && (
            <div className="noam-card p-4" data-testid="dealscore-actions">
              <div className="text-[10px] uppercase tracking-widest text-[#1D9E75] mb-2 flex items-center gap-1.5">
                <ArrowUpRight className="w-3.5 h-3.5" /> Repositionnement recommandé
              </div>
              <ul className="space-y-2">
                {score.actions.map((a, i) => (
                  <li key={i} className="text-xs">
                    <span className="text-zinc-200 font-medium">{a.action}</span>
                    {a.impact && <div className="text-[10px] text-zinc-500 flex items-center gap-1 mt-0.5"><ShieldCheck className="w-3 h-3 text-[#1D9E75]" /> {a.impact}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
